import * as React from 'react';
import { Link } from 'react-router';
import { __, __r } from 'react-i18n';
import { FlexRow, FlexColCenter } from '../../Defaults/Flexbox/Flexbox';
import styled, { withTheme } from 'styled-components';
import { rem } from 'polished';
import { IconImg } from '../../Defaults/Icon/IconImg';
import * as homeSrc from '../../../../assets/zemplin/home.svg';
import { TextP } from '../../Defaults/Text';
import { ZemplinLoaderWrapper } from '../../..';
import { getFinishedOrderInfo } from '../../../utilities/selectors';
import { InfoText, InfoType } from '../../Zemplin/General/InfoText';
import { IconType, SvgIcon } from '../../Zemplin/General/SvgIcon';

interface Props {
  theme: any;
  order: any;
  isFetching: boolean;
  redirectUrl: string;
  status: string;
}

function FinishedOrder({
  theme,
  order,
  isFetching,
  redirectUrl,
  status,
}: Props) {
  const {
    orderNr,
    name,
    surname,
    phone,
    city,
    country,
    streetNumber,
    street,
    zip,
    paymentType,
    orderPaidDatetime,
  } = getFinishedOrderInfo(order);

  React.useEffect(() => {
    if (paymentType === 'KARTA' && !status && !orderPaidDatetime) {
      window.location.href = redirectUrl;
    }
  }, [paymentType, status, orderPaidDatetime]);

  return (
    <>
      <StatusWrapper>
        {status ? (
          status === 'success' ? (
            <InfoText
              type={InfoType.SUCCESS}
              info={__('Objednávka bola úspešne zaplatená')}
            />
          ) : (
            <InfoText
              type={InfoType.ERROR}
              info={__(
                'Objednávku sa nepodarilo zaplatiť. Na email Vám bol zaslaný link, kde sa môžete znova pokúsiť o platbu.',
              )}
            />
          )
        ) : null}
      </StatusWrapper>
      <Wrapper>
        {isFetching ? (
          <ZemplinLoaderWrapper />
        ) : (
          <>
            <LeftWrapper>
              <SvgIcon
                width={48}
                height={48}
                icon={IconType.finishedOrder}
                alt={''}
                viewBox="0 0 48 48"
                fill={theme.colors.stockCountAvailable}
                marginLeft={0}
              />

              <Title>
                {order && (order as any).is_demand
                  ? __('Dokončený dopyt')
                  : __('Ďakujeme za váš nákup!')}
              </Title>
              <OrderNr>{`${
                order && (order as any).is_demand
                  ? __('Dopyt')
                  : __('Objednávka')
              } č.  ${orderNr}`}</OrderNr>
              <SmallText>
                {order && (order as any).is_demand
                  ? __(
                      'Na vašu emailovú adresu bolo zaslané potvrdenie o prijatí dopytu. \n Ohľadom dostupnosti tovaru Vás budeme kontaktovať.',
                    )
                  : __(
                      'Na vašu emailovú adresu bolo zaslané potvrdenie o prijatí objednávky. \n Stav môžete sledovať v detaile objednávky vo vašom účte.',
                    )}
              </SmallText>
              <ImportOrderButton to={'/'}>
                <IconImg
                  src={homeSrc.toString()}
                  width={16}
                  height={16}
                  alt="Ikonka domu"
                  cursor="pointer"
                />{' '}
                <ImportOrderTitle>
                  {__('Prejsť na hlavnú stránku')}
                </ImportOrderTitle>
              </ImportOrderButton>
            </LeftWrapper>
            <RightWrapper>
              <OrderNr>{__('Vami zadaná adresa')}</OrderNr>
              <AddressInfo>{`${name} ${surname}`}</AddressInfo>
              <AddressInfo>{`${street} ${streetNumber}, ${zip} ${city}`}</AddressInfo>
              <AddressInfo>{country}</AddressInfo>
              <br />
              <Phone>{phone}</Phone>
              <InfoText
                noIcon={true}
                type={InfoType.INFO}
                info={__(
                  'V prípade nesprávnych údajov nás prosím kontaktujte.',
                )}
                marginBottom={0}
              />
            </RightWrapper>
          </>
        )}
      </Wrapper>
    </>
  );
}

const Wrapper = styled(FlexRow)`
  width: 100%;
  margin: 0 auto;
  padding: ${rem(80)} 0;
  max-width: ${rem(1180)};
  justify-content: space-between;

  ${({ theme }) => theme.mediab.m810`
    flex-flow: column;
    align-items: center;
    padding: ${rem(32)} 0 ${rem(56)};
  `}
`;

const LeftWrapper = styled(FlexColCenter)`
  width: 100%;
  padding: ${rem(24)} 0;
  margin: 0;
  margin-right: ${rem(48)};
  height: fit-content;
  max-width: ${rem(480)};

  ${({ theme }) => theme.mediab.m810`
    padding: 0;
    margin-right: 0;
  `}
`;

const RightWrapper = styled(LeftWrapper)`
  padding: ${rem(32)};
  border: ${({ theme }) => theme.borders.weak};
  align-items: flex-start;
  margin-right: 0;

  ${({ theme }) => theme.mediab.m810`
    border: 0;
    padding: 0;
  `}
`;

const Title = styled.h2`
  margin: ${rem(32)} 0;
  font-size: ${rem(30)};
  font-family: ${({ theme }) => theme.font.primary};
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: ${rem(38)};
  font-weight: 400;

  ${({ theme }) => theme.mediab.m810`
    font-size: ${rem(24)};
    line-height: ${rem(32)};
    text-align: center;
  `}
`;

const SmallText = styled(TextP)`
  font-weight: 400;
  font-family: ${({ theme }) => theme.font.secondaryLight};
  line-height: ${rem(22)};
  font-size: ${rem(14)};
  margin-bottom: ${rem(32)};
  text-align: center;
`;

const AddressInfo = styled(SmallText)`
  margin-bottom: 0;
`;

const Phone = styled(AddressInfo)`
  margin-bottom: ${rem(32)};
`;

const OrderNr = styled(SmallText)`
  font-weight: 400;
  font-family: ${({ theme }) => theme.font.secondary};
`;

const ImportOrderButton = styled(Link)`
  cursor: pointer;
  color: ${({ theme }) => theme.colors.black};
  background-color: ${({ theme }) => theme.colors.white};
  font-size: ${rem(14)};
  padding: ${rem(8)};
  line-height: ${rem(22)};
  height: ${rem(48)};
  display: flex;
  align-items: center;
  text-decoration: none;
  border: ${({ theme }) => theme.borders.primary};

  max-width: ${rem(262)};
  width: 100%;
  display: flex;
  justify-content: center;
  max-height: ${rem(62)};
  transition: filter 0.2s ease;

  &:hover {
    filter: brightness(90%);
  }

  ${({ theme }) => theme.mediab.m810`
      max-width: ${rem(288)};
      margin-bottom: ${rem(56)};
  `}
`;

const ImportOrderTitle = styled.span`
  margin-left: ${rem(16)};
  display: flex;
  align-items: flex-end;
`;

const StatusWrapper = styled.div`
  margin-top: ${rem(24)};
`;

const WithTheme: any = withTheme(FinishedOrder);
export { WithTheme as FinishedOrder };
